import { useState } from "react";
import { api } from "../lib/api";
import type { User } from "@flowgen/shared";

interface Props {
  onAuthed: (user: User) => void;
}

export function AuthScreen({ onAuthed }: Props) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const res = await api.post<{ token: string; user: User }>(path, {
        email: email.trim(),
        password,
      });
      api.setToken(res.token);
      onAuthed(res.user);
    } catch (err: any) {
      const code = err?.message;
      if (code === "email_taken") setError("That email is already registered.");
      else if (code === "invalid_credentials") setError("Wrong email or password.");
      else if (code === "invalid_input") setError("Use a valid email and a password of at least 8 characters.");
      else if (code === "api_html_response") {
        setError(
          "Can't reach the API (got a web page instead of JSON). On the Railway frontend service, set VITE_API_BASE or API_BASE to your backend URL ending in /api (e.g. https://YOUR-BACKEND.up.railway.app/api), then redeploy the frontend."
        );
      } else setError(err?.message ?? "Failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: "var(--bg)",
      }}
    >
      <div className="card" style={{ width: 400, maxWidth: "100%", padding: 24 }}>
        <h2 style={{ marginTop: 0 }}>{mode === "login" ? "Sign in" : "Create account"}</h2>
        <div className="sub" style={{ marginBottom: 16 }}>
          Projects and media stay under your account on this server.
        </div>
        <div className="field">
          <label>Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
          />
        </div>
        <div className="field">
          <label>Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            onKeyDown={(e) => {
              if (e.key === "Enter" && email.trim() && password.length >= 8) submit();
            }}
          />
        </div>
        {error && (
          <div style={{ color: "var(--danger)", marginBottom: 12, fontSize: 13 }}>{error}</div>
        )}
        <button
          className="primary"
          style={{ width: "100%" }}
          disabled={busy || !email.trim() || password.length < 8}
          onClick={submit}
        >
          {busy ? "…" : mode === "login" ? "Sign in" : "Create account"}
        </button>
        <div className="tiny muted" style={{ marginTop: 14, textAlign: "center" }}>
          {mode === "login" ? (
            <>
              No account?{" "}
              <button type="button" style={{ padding: 0, border: "none", background: "none", color: "var(--accent)", cursor: "pointer" }} onClick={() => { setMode("register"); setError(null); }}>
                Create one
              </button>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <button type="button" style={{ padding: 0, border: "none", background: "none", color: "var(--accent)", cursor: "pointer" }} onClick={() => { setMode("login"); setError(null); }}>
                Sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
